import { useEffect, useRef, useState } from "react";
import {
  ALFABETY,
  DOMYSLNA_DLUGOSC,
  NAZWY_KLAS,
  OPISY_RANGI,
  SLOWNIE_DLUGOSC,
  type Dlugosc,
  type Jezyk,
  type Klasa,
  literyAlfabetu,
  nastepnyOgonek,
  opisRangi,
} from "@gierki/shared";
import { vibrate } from "../hooks.ts";
import { Karta, Komunikat, Odznaka, Przycisk } from "../ui.tsx";
import { Klawiatura, koloryKlawiszy, type KolorLitery } from "./Klawiatura.tsx";

/**
 * Wordle — cztery widoki w jednym pliku: lobby, runda, wyniki, koniec.
 *
 * Cała wizualna robota tej gry to typografia i trzy kolory. Grafiki nie ma
 * i nie będzie: kafelek Wordle'a nie ma fizycznego pierwowzoru, a tekstura
 * zabrałaby kolor, który tu jest **całą treścią gry**.
 *
 * Kolor jest mimo to **drugim kanałem, nie jedynym**: „obecna" nosi kropkę
 * w rogu, „trafiona" jest pełna i bez znacznika, „nieobecna" jest przygaszona.
 * Da się rozegrać rundę, czytając same kształty — a zielony obok żółtego to
 * dokładnie ta para, której nie odróżnia deuteranop, więc pary tu nie ma.
 */

const NAZWA_JEZYKA: Record<string, string> = {
  pl: "po polsku",
  en: "po angielsku",
  es: "po hiszpańsku",
};

const TLO: Record<KolorLitery, string> = {
  trafiona: "var(--wordle-trafiona)",
  obecna: "var(--wordle-obecna)",
  nieobecna: "var(--wordle-nieobecna)",
};

/**
 * Rozmiar kafelka: **rachunek, nie trzy wpisane z palca liczby**.
 *
 * Geometria ekranu, na którym się gra: 390 px szerokości (iPhone SE/13 mini —
 * najwęższy telefon w rodzinie), 16 px marginesu strony z każdej strony
 * (`px-4` powłoki), odstęp między kafelkami 6 px (`gap-1.5`). Na rząd zostaje
 * **358 px**, a przy `n` kafelkach jeden ma najwyżej `(358 − 6(n−1)) / n`:
 *
 * | `n` | 4 | 5 | 6 |
 * |---|---|---|---|
 * | mieści się | 85,0 | 66,8 | **54,7** |
 * | bierzemy   | 64   | 56   | **52**   |
 *
 * Sześć kafelków jest tu jedynym realnym problemem i to ono wyznacza dolną
 * granicę: 52 px daje 6×52 + 5×6 = **342 px**, czyli 16 px zapasu — tyle, żeby
 * rząd przeżył telefon 360-piksélowy (tam limit to 50,3 px, a `max-w` niżej
 * dociska kafelek do 49,7). Poniżej 48 px litera w `text-2xl` przestaje mieć
 * margines i kafelek zaczyna wyglądać jak przycisk.
 *
 * Czterech kafelków **nie zostawiamy w rozmiarze pięciu**: 4×56 + 18 = 242 px
 * na 358 px to rząd zgubiony pośrodku ekranu. 64 px daje 274 px, czyli mniej
 * więcej tę samą „wagę" na ekranie, co pięć po 56 (302 px).
 *
 * `max-w` w procentach jest **drugą, węższą granicą** dla ekranów poniżej
 * 390 px: `maxw ≤ (W − 6(n−1)) / (n·W)`, dla W = 358 wychodzi 23,7% / 18,7% /
 * 15,3%. Bierzemy 22 / 18 / 15 — wtedy na każdej szerokości wygrywa ta granica,
 * która naprawdę ogranicza, i rząd nigdy nie wypycha strony w poziom.
 */
const KAFELEK: Record<number, string> = {
  4: "w-16 max-w-[22%]",
  5: "w-14 max-w-[18%]",
  6: "w-[3.25rem] max-w-[15%]",
};

const klasaKafelka = (dlugosc: number): string => KAFELEK[dlugosc] ?? KAFELEK[DOMYSLNA_DLUGOSC] ?? "";

const OPIS_STANU: Record<string, string> = {
  gra: "gra",
  trafil: "trafił",
  spalil: "spalił",
  poddal: "poddał się",
};

export interface WordleProba {
  slowo: string;
  kolory: string[];
}

export interface WordleView {
  faza: "lobby" | "runda" | "wyniki" | "koniec";
  jezyk: string;
  /** Ile liter ma hasło: 4, 5 albo 6. Bez tego klient nie wie, co narysować. */
  dlugosc: number;
  /** Czy gra się w pełnej pisowni. Decyduje o alfabecie i o klawiszu ogonka. */
  ogonki: boolean;
  czesciMowy: string[];
  /** Próg popularności haseł — ustawienie z lobby, nie ranga wylosowanego hasła. */
  maxRanga: number;
  ilePuli: number | null;
  roundNo: number;
  roundCount: number;
  probyNaRunde: number;
  pokazujPostep: boolean;
  deadlineAt: number | null;
  hostId: string;
  players: string[];
  sumy: Record<string, number>;
  historia: Array<{
    numer: number;
    haslo: string;
    trafil: Record<string, number | null>;
    kolejnosc: Record<string, number | null>;
    punkty: Record<string, number>;
  }>;
  mojeProby: WordleProba[];
  mojTrafil: number | null;
  skonczylem: boolean;
  postep: Array<{ playerId: string; uzyte: number; stan: string }>;
  /** Pisownia — wyłącznie w fazie `wyniki` i `koniec`. */
  haslo: string | null;
  /** Ranga odsłoniętego hasła; `null` w trakcie rundy i przy haśle spoza listy częstości. */
  rangaHasla: number | null;
  canStart: boolean;
}

export function Wordle({
  view,
  nicki,
  mojeId,
  onStart,
  onZgadnij,
  onPoddaj,
  onWroc,
}: {
  view: WordleView;
  nicki: Record<string, string>;
  mojeId: string;
  onStart: () => void;
  onZgadnij: (slowo: string) => void;
  onPoddaj: () => void;
  onWroc: () => void;
}) {
  switch (view.faza) {
    case "lobby":
      return <Lobby view={view} nicki={nicki} onStart={onStart} />;
    case "runda":
      return <Runda view={view} nicki={nicki} mojeId={mojeId} onZgadnij={onZgadnij} onPoddaj={onPoddaj} />;
    default:
      return <Wyniki view={view} nicki={nicki} mojeId={mojeId} onStart={onStart} onWroc={onWroc} />;
  }
}

/* ————————————————————————— lobby ————————————————————————— */

function Lobby({
  view,
  nicki,
  onStart,
}: {
  view: WordleView;
  nicki: Record<string, string>;
  onStart: () => void;
}) {
  const klasy = view.czesciMowy.length === 0 ? "wszystkie części mowy" : view.czesciMowy
    .map((k) => NAZWY_KLAS[k as Klasa] ?? k)
    .join(", ");

  return (
    <>
      <Komunikat>
        Wszyscy zgadują naraz to samo hasło — ma{" "}
        {SLOWNIE_DLUGOSC[view.dlugosc as Dlugosc] ?? `${view.dlugosc} liter`}. {view.probyNaRunde}{" "}
        prób, kafelki
        kolorują się po każdej. Wygrywa ten, kto trafi w najmniejszej liczbie prób. Hasło
        odsłania się dopiero po całej rundzie — kto trafił, czeka.
      </Komunikat>

      <Karta>
        <div className="font-semibold">
          Gramy {NAZWA_JEZYKA[view.jezyk] ?? ""}, hasło ma{" "}
          {SLOWNIE_DLUGOSC[view.dlugosc as Dlugosc] ?? `${view.dlugosc} liter`}
        </div>
        <div className="text-sm text-tekst-slaby">{klasy}</div>
        <div className="text-sm text-tekst-slaby">
          {view.ogonki
            ? "Z ogonkami — hasło może mieć ą, ę, ł. Na klawiaturze jest klawisz, który dokłada ogonek do ostatniej litery."
            : "Bez ogonków — hasłem będzie słowo, które ogonków nie ma w ogóle."}
        </div>
        <div className="text-sm text-tekst-slaby">
          Popularność haseł: {OPISY_RANGI[view.maxRanga] ?? view.maxRanga}
        </div>
        {view.ilePuli !== null ? (
          <div className="text-sm text-tekst-slaby">Hasła do wylosowania: {view.ilePuli}</div>
        ) : null}
        <div className="mt-2 text-sm text-tekst-slaby">
          {view.players.map((gracz) => nicki[gracz] ?? gracz).join(", ")}
        </div>
      </Karta>

      {view.canStart ? (
        <Przycisk wariant="glowny" pelna onClick={onStart}>
          Zaczynamy
        </Przycisk>
      ) : (
        <Komunikat>Czekamy, aż host zacznie.</Komunikat>
      )}
    </>
  );
}

/* ————————————————————————— runda ————————————————————————— */

/**
 * Jedna wibracja przy trafieniu, **raz na rundę**.
 *
 * Wyzwalaczem jest numer rundy zapamiętany w refie, a nie zmiana `mojTrafil` —
 * ta sama pułapka, co z kością pokazującą piątkę drugi raz i z `useAnimacjaRzutu`
 * w Kościach. Serwer przysyła widok przy każdej cudzej próbie, więc efekt
 * zależny od samej wartości odpaliłby wibrację przy każdym strzale sąsiada:
 * `mojTrafil` zostaje wtedy takie samo, ale efekt i tak biegnie na nowej
 * referencji widoku. Ref z numerem rundy jest odporny i na to, i na powtórzenie
 * tej samej liczby prób w kolejnej rundzie.
 */
function useHaptykaTrafienia(roundNo: number, mojTrafil: number | null): void {
  const zawibrowano = useRef<number | null>(null);
  useEffect(() => {
    if (mojTrafil === null || zawibrowano.current === roundNo) return;
    zawibrowano.current = roundNo;
    // Krótko-przerwa-dłużej: to samo „udało się", co przy skompletowanym stadzie
    // w Superfarmerze. Telefon bywa w uchwycie, a gracz patrzy na drogę.
    vibrate([15, 45, 30]);
  }, [roundNo, mojTrafil]);
}

function Runda({
  view,
  nicki,
  mojeId,
  onZgadnij,
  onPoddaj,
}: {
  view: WordleView;
  nicki: Record<string, string>;
  mojeId: string;
  onZgadnij: (slowo: string) => void;
  onPoddaj: () => void;
}) {
  const [wpisane, setWpisane] = useState<string[]>([]);
  const rundaRef = useRef(view.roundNo);
  const ileProbRef = useRef(view.mojeProby.length);

  useHaptykaTrafienia(view.roundNo, view.mojTrafil);

  useEffect(() => {
    if (rundaRef.current === view.roundNo) return;
    rundaRef.current = view.roundNo;
    setWpisane([]);
  }, [view.roundNo]);

  /*
   * Bufor czyścimy **dopiero, gdy próba przeszła** — czyli gdy przybyło ich
   * w widoku. Odrzucone słowo („nie ma takiego słowa") zostaje na kafelkach:
   * gracz ma poprawić jedną literę, a nie wpisywać wszystko od nowa.
   */
  useEffect(() => {
    if (ileProbRef.current === view.mojeProby.length) return;
    ileProbRef.current = view.mojeProby.length;
    setWpisane([]);
  }, [view.mojeProby.length]);

  const jezyk = (view.jezyk as Jezyk) in ALFABETY ? (view.jezyk as Jezyk) : "pl";
  const alfabet = literyAlfabetu(jezyk, view.ogonki);
  const kolory = koloryKlawiszy(view.mojeProby);

  const wyslij = () => {
    if (wpisane.length !== view.dlugosc) return;
    onZgadnij(wpisane.join(""));
  };

  /*
   * Klawisz ogonka zamienia **ostatnią wpisaną literę** na jej kolejny wariant.
   * Podgląd liczymy tu, żeby klawisz mógł pokazać wynik, zamiast nazywać
   * funkcję: po `l` widnieje na nim `ł`, po `ź` — `ż`. Bez tego cykl `z → ź → ż`
   * wymagałby pamiętania, w którym miejscu się jest.
   *
   * Klawisza nie ma w partii bez ogonków ani w angielskiej (`WARIANTY_OGONKA.en`
   * jest puste, więc podgląd zawsze byłby `null`).
   */
  const ostatnia = wpisane[wpisane.length - 1];
  const podgladOgonka =
    view.ogonki && ostatnia !== undefined ? nastepnyOgonek(ostatnia, jezyk) : null;
  const dajOgonek =
    view.ogonki && alfabet.length > ALFABETY[jezyk].length
      ? () =>
          setWpisane((biezace) => {
            const litera = biezace[biezace.length - 1];
            if (litera === undefined) return biezace;
            const wariant = nastepnyOgonek(litera, jezyk);
            return wariant === null ? biezace : [...biezace.slice(0, -1), wariant];
          })
      : undefined;

  const zostalo = view.probyNaRunde - view.mojeProby.length;

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="font-semibold">
          Runda {view.roundNo}/{view.roundCount}
        </div>
        {view.pokazujPostep ? (
          <Odznaka>{view.skonczylem ? "skończone" : `${zostalo} prób`}</Odznaka>
        ) : null}
      </div>

      <Siatka view={view} wpisane={wpisane} />

      {view.skonczylem ? (
        <Komunikat>
          {view.mojTrafil !== null
            ? `Jest! Za ${view.mojTrafil}. Czekamy na resztę — hasło zobaczysz po rundzie.`
            : "Runda jest dla ciebie skończona. Hasło zobaczysz po rundzie."}
        </Komunikat>
      ) : null}

      {view.pokazujPostep ? <Postep view={view} nicki={nicki} mojeId={mojeId} /> : null}

      <div className="sticky bottom-0 pt-2 bezpieczny-dol-waski bg-tlo space-y-2">
        {view.skonczylem ? null : (
          <Klawiatura
            litery={alfabet}
            kolory={kolory}
            zablokowana={false}
            onLitera={(litera) =>
              setWpisane((biezace) =>
                biezace.length >= view.dlugosc ? biezace : [...biezace, litera],
              )
            }
            onKasuj={() => setWpisane((biezace) => biezace.slice(0, -1))}
            onZatwierdz={wyslij}
            onOgonek={dajOgonek}
            podgladOgonka={podgladOgonka}
          />
        )}
        {view.skonczylem ? null : <Poddanie onPoddaj={onPoddaj} />}
      </div>
    </>
  );
}

/**
 * „Poddaję" **z potwierdzeniem, które wygasa**.
 *
 * Przycisk stoi pod klawiaturą i jednym dotknięciem kończył rundę — a rundy nie
 * da się cofnąć, więc pomyłkowe dotknięcie kosztowało cały strzał i zero
 * punktów. Wzorzec jest z `Home.tsx` („Usuń" → „Na pewno?"): drugie dotknięcie
 * tego samego przycisku czyta się jednoznacznie, a systemowe `window.confirm`
 * w zainstalowanej aplikacji wygląda jak błąd przeglądarki.
 *
 * **Wygaśnięcie po czterech sekundach jest częścią tego wzorca, nie ozdobą.**
 * Potwierdzenie, które zostaje uzbrojone, zamienia dwa przypadkowe dotknięcia
 * rozłożone w czasie w poddaną rundę — czyli oddaje dokładnie ten błąd, przed
 * którym miało chronić. Ta sama liczba i ten sam mechanizm, co przy kasowaniu
 * partii; różnica byłaby drugą stałą do uzgadniania.
 */
function Poddanie({ onPoddaj }: { onPoddaj: () => void }) {
  const [pewny, setPewny] = useState(false);

  useEffect(() => {
    if (!pewny) return;
    const timer = setTimeout(() => setPewny(false), 4000);
    return () => clearTimeout(timer);
  }, [pewny]);

  return (
    <Przycisk
      // Uzbrojony przycisk zmienia wariant na `blad`: samo „Na pewno?" bez
      // zmiany tła da się przeoczyć kątem oka w jadącym aucie.
      wariant={pewny ? "blad" : "cichy"}
      pelna
      onClick={() => {
        if (pewny) onPoddaj();
        else setPewny(true);
      }}
    >
      {pewny ? "Na pewno? Runda za zero punktów" : "Poddaję"}
    </Przycisk>
  );
}

/**
 * Siatka prób. **Stała wysokość od pierwszej klatki**, żeby klawiatura nie
 * wędrowała po ekranie z każdą próbą — a kafelki nie są celem dotykowym, więc
 * próg 56 px ich nie dotyczy; dotyczy ich czytelność.
 */
function Siatka({ view, wpisane }: { view: WordleView; wpisane: string[] }) {
  const rzedy = Array.from({ length: view.probyNaRunde }, (_, i) => i);
  const biezacy = view.mojeProby.length;

  return (
    <div className="space-y-1.5">
      {rzedy.map((indeks) => {
        const proba = view.mojeProby[indeks];
        const litery =
          proba !== undefined
            ? [...proba.slowo]
            : indeks === biezacy
              ? wpisane
              : [];

        /*
         * Rząd trafiony podskakuje po kolei, **po** zakończeniu obrotu. Klasę
         * dokładamy w tym samym renderze, w którym pojawia się rząd, więc obie
         * animacje startują raz i nie odpalają się przy kolejnych widokach
         * z serwera — inaczej rząd podskakiwałby przy każdym strzale sąsiada.
         */
        const trafiony = proba?.kolory.every((k) => k === "trafiona") === true;

        return (
          <div key={indeks} className="flex justify-center gap-1.5">
            {Array.from({ length: view.dlugosc }, (_, pozycja) => {
              const litera = litery[pozycja] ?? "";
              const kolor = proba?.kolory[pozycja] as KolorLitery | undefined;
              return (
                <div
                  key={pozycja}
                  /*
                   * Klucz `key` rzędu jest numerem rzędu, a animację odpala
                   * `key` na wewnętrznym elemencie z numerem próby: wpisanie
                   * dwa razy z rzędu tego samego słowa przy porównywaniu
                   * wartości wyglądałoby jak „nic się nie stało" i rząd
                   * zastygłby bez animacji.
                   */
                  className={
                    `aspect-square ${klasaKafelka(view.dlugosc)} rounded-lg flex items-center ` +
                    "justify-center text-2xl font-bold uppercase relative " +
                    (kolor === undefined
                      ? "border-2 border-obrys"
                      : trafiony
                        ? "wordle-obrot wordle-skok"
                        : "wordle-obrot")
                  }
                  style={
                    kolor === undefined
                      ? { backgroundColor: "var(--wordle-pusty)", color: "var(--wordle-napis)" }
                      : {
                          backgroundColor: TLO[kolor],
                          color: "var(--wordle-napis)",
                          ["--wordle-opoznienie" as string]: `${pozycja * 90}ms`,
                          opacity: kolor === "nieobecna" ? 0.75 : 1,
                        }
                  }
                >
                  {litera}
                  {/* Drugi kanał obok koloru: „obecna" nosi kropkę w rogu. */}
                  {kolor === "obecna" ? (
                    <span
                      className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: "var(--wordle-napis)" }}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Postęp pozostałych: **liczba prób tak, kolory nie**.
 *
 * Liczba jest presją bez treści — „mama jest na czwartej próbie" motywuje
 * i niczego nie podpowiada. Kolory podpowiadają, i to mocno: gramy w jednym
 * samochodzie, a ekran sąsiada jest w zasięgu wzroku.
 */
function Postep({
  view,
  nicki,
  mojeId,
}: {
  view: WordleView;
  nicki: Record<string, string>;
  mojeId: string;
}) {
  return (
    <Karta>
      <div className="text-sm text-tekst-slaby">Kto gdzie jest</div>
      <div className="mt-2 space-y-1">
        {view.postep.map((wpis) => (
          <div
            key={wpis.playerId}
            className={
              "flex justify-between " +
              (wpis.playerId === mojeId ? "text-akcent font-semibold" : "")
            }
          >
            <span>{nicki[wpis.playerId] ?? wpis.playerId}</span>
            <span className="tabular">
              {wpis.uzyte}/{view.probyNaRunde} · {OPIS_STANU[wpis.stan] ?? wpis.stan}
            </span>
          </div>
        ))}
      </div>
    </Karta>
  );
}

/* ————————————————————————— wyniki ————————————————————————— */

function Wyniki({
  view,
  nicki,
  mojeId,
  onStart,
  onWroc,
}: {
  view: WordleView;
  nicki: Record<string, string>;
  mojeId: string;
  onStart: () => void;
  onWroc: () => void;
}) {
  const koniec = view.faza === "koniec";
  const runda = view.historia[view.historia.length - 1];

  const tabela = view.players
    .map((gracz) => ({ gracz, punkty: view.sumy[gracz] ?? 0 }))
    .sort((a, b) => b.punkty - a.punkty);

  return (
    <>
      {view.haslo !== null ? (
        <Karta>
          <div className="text-sm text-tekst-slaby">Hasło rundy {runda?.numer ?? view.roundNo}</div>
          <div className="text-3xl font-bold uppercase tracking-widest">{view.haslo}</div>
          {/*
            * Zdania „ogonki nie liczą się w grze" tu **nie ma i być nie może**.
            * Stało tu, dopóki gra składała pisownię; dziś hasło jest dokładnie
            * tym, czym jest, i pokazujemy je bez komentarza. Wiersz wyjaśniający
            * pisownię byłby po tej zmianie kłamstwem.
            */}
          {/*
            * Ranga stoi tu po to, żeby dało się kalibrować ustawienie
            * popularności: po rundzie, w której padło coś, czego nikt nie znał,
            * widać „bardzo rzadkie, ranga 129 276" i wiadomo, że przy następnej
            * partii trzeba suwak przesunąć w lewo. `opisRangi` zwraca `null` dla
            * hasła spoza listy częstości — wtedy nie ma tu żadnego wiersza,
            * zamiast „ranga 0", która skłamałaby w drugą stronę.
            */}
          {opisRangi(view.rangaHasla) !== null ? (
            <div className="mt-1 text-sm text-tekst-slaby">{opisRangi(view.rangaHasla)}</div>
          ) : null}
        </Karta>
      ) : null}

      {runda !== undefined ? (
        <Karta>
          <div className="text-sm text-tekst-slaby">Ta runda</div>
          <div className="mt-2 space-y-1">
            {view.players.map((gracz) => {
              const trafil = runda.trafil[gracz] ?? null;
              return (
                <div
                  key={gracz}
                  className={
                    "flex justify-between " +
                    (gracz === mojeId ? "text-akcent font-semibold" : "")
                  }
                >
                  <span>{nicki[gracz] ?? gracz}</span>
                  <span className="tabular">
                    {trafil === null ? "nie trafił" : `za ${trafil}`} · {runda.punkty[gracz] ?? 0}
                  </span>
                </div>
              );
            })}
          </div>
        </Karta>
      ) : null}

      <Karta>
        <div className="text-sm text-tekst-slaby">{koniec ? "Koniec partii" : "Razem"}</div>
        <div className="mt-2 space-y-1">
          {tabela.map((wiersz) => (
            <div
              key={wiersz.gracz}
              className={
                "flex justify-between " +
                (wiersz.gracz === mojeId ? "text-akcent font-semibold" : "")
              }
            >
              <span>{nicki[wiersz.gracz] ?? wiersz.gracz}</span>
              <span className="tabular">{wiersz.punkty}</span>
            </div>
          ))}
        </div>
      </Karta>

      {koniec ? (
        <Przycisk wariant="glowny" pelna onClick={onWroc}>
          Wróć do listy
        </Przycisk>
      ) : view.canStart ? (
        <Przycisk wariant="glowny" pelna onClick={onStart}>
          Następna runda
        </Przycisk>
      ) : (
        <Komunikat>Czekamy, aż host zacznie następną rundę.</Komunikat>
      )}
    </>
  );
}
